import React, { useMemo, useState } from "react";
import Pagination from "react-js-pagination";
import useMountEffect from "./CustomHooks/useMountEffect";
import APISESSION from "../api/ApiSession";
import DscTable from "./DscPages/DscTable";
import FuzzySearchInput from "../components/common/FuzzySearchInput";
import { sortItems } from "../utils/sortItems";
import { fuseFilter } from "../utils/fuseSearch";
import { exportToCsv } from "../utils/exportCsv";
// import { exportToPdf } from "../utils/exportPdf";
const apiSession = new APISESSION();

export const DSC_SEARCH_KEYS = [
  "subscriptionNumber",
  "customerName",
  "customerPaymentRef",
  "costTypeReference",
  "costType",
  "shipToEmail",
  "offerName",
  "currentTerm",
  "termStartDate",
  "termEndDate",
  "status",
  "productList.name",
  "productList.sku",
  "productList.type",
  "soldToCountry",
  "soldToCity",
  "soldToAddress1",
  "soldToZipCode",
  "soldToWorkEmail",
  "soldToWorkPhone",
  "soldToOtherPhone",
  "soldTo",
];

export function safeJsonParse(value) {
  if (!value || typeof value !== "string") return null;
  try {
    return JSON.parse(value);
  } catch (_e) {
    return null;
  }
}

export function formatSoldToSummary(soldTo) {
  const addressParts = [soldTo?.address1, soldTo?.city, soldTo?.country, soldTo?.zipCode]
    .map((v) => (typeof v === "string" ? v.trim() : ""))
    .filter(Boolean);

  const contactParts = [
    soldTo?.workEmail ? `Work Email: ${String(soldTo.workEmail).trim()}` : "",
    soldTo?.workPhone ? `Work Phone: ${String(soldTo.workPhone).trim()}` : "",
    soldTo?.otherPhone ? `Other Phone: ${String(soldTo.otherPhone).trim()}` : "",
  ].filter(Boolean);

  return [...addressParts, ...contactParts].join(" | ");
}

export function enrichDscRow(row) {
  const item = row && typeof row === "object" ? row : {};
  const details = safeJsonParse(item?.details);
  const soldTo = details?.soldToContact;

  return {
    ...item,
    soldToCountry: soldTo?.country ?? "",
    soldToCity: soldTo?.city ?? "",
    soldToAddress1: soldTo?.address1 ?? "",
    soldToZipCode: soldTo?.zipCode ?? "",
    soldToWorkEmail: soldTo?.workEmail ?? "",
    soldToWorkPhone: soldTo?.workPhone ?? "",
    soldToOtherPhone: soldTo?.otherPhone ?? "",
    soldTo: formatSoldToSummary(soldTo),
  };
}

export function parseDateInputToLocalMs(value, endOfDay = false) {
  if (!value || typeof value !== "string") return null;
  const v = value.trim();
  if (!v) return null;

  // Supports: YYYY-MM-DD, DD-MM-YYYY, DD/MM/YYYY
  let year;
  let month;
  let day;

  let m = v.match(/^\s*(\d{4})-(\d{1,2})-(\d{1,2})\s*$/);
  if (m) {
    year = Number(m[1]);
    month = Number(m[2]);
    day = Number(m[3]);
  } else {
    m = v.match(/^\s*(\d{1,2})[\/-](\d{1,2})[\/-](\d{4})\s*$/);
    if (!m) return null;
    day = Number(m[1]);
    month = Number(m[2]);
    year = Number(m[3]);
  }

  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) {
    return null;
  }
  if (month < 1 || month > 12 || day < 1 || day > 31) return null;

  const d = endOfDay
    ? new Date(year, month - 1, day, 23, 59, 59, 999)
    : new Date(year, month - 1, day, 0, 0, 0, 0);

  const t = d.getTime();
  // Validate we didn't roll over (e.g., 31/02/2026).
  if (
    d.getFullYear() !== year ||
    d.getMonth() !== month - 1 ||
    d.getDate() !== day ||
    !Number.isFinite(t)
  ) {
    return null;
  }

  return t;
}

export const SORT_STATES = [
  {
    key: "termStartDate",
    type: "date",
    direction: "asc",
    label: "Activation Start Date",
  },
  {
    key: "termStartDate",
    type: "date",
    direction: "desc",
    label: "Activation Start Date",
  },
  {
    key: "termStartDate",
    type: "date",
    direction: "desc",
    label: "Last Week",
    windowMs: 7 * 24 * 60 * 60 * 1000,
  },
  {
    key: "termStartDate",
    type: "date",
    direction: "desc",
    label: "Last Month",
    windowMs: 30 * 24 * 60 * 60 * 1000,
  },
  {
    key: "termStartDate",
    type: "date",
    direction: "desc",
    label: "Last Year",
    windowMs: 365 * 24 * 60 * 60 * 1000,
  },
  {
    key: "subscriptionNumber",
    type: "string",
    direction: "asc",
    label: "Subscription No",
  },
  {
    key: "subscriptionNumber",
    type: "string",
    direction: "desc",
    label: "Subscription No",
  },
  {
    key: "customerName",
    type: "string",
    direction: "asc",
    label: "Customer Name",
  },
  {
    key: "customerName",
    type: "string",
    direction: "desc",
    label: "Customer Name",
  },
];

const Dsc = () => {

  const [searchInput, setSearchInput] = useState("");
  const [dscData, setDscData] = useState([]);
  const [filteredDscData, setFilteredDscData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(false);
  const [indexValue, setIndexValue] = useState("");
  const [expandedRow, setExpandedRow] = useState(-1);
  // Default: termStartDate Desc
  const [sortStateIndex, setSortStateIndex] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [tableRows, setTableRows] = useState({
    activePage: 1,
    itemSliceFirst: 0,
    itemSliceLast: 10,
    itemsPerPage: 10,
  })

  const sortConfig = useMemo(() => {
    const s = SORT_STATES[sortStateIndex] || SORT_STATES[0];
    return {
      key: s.key,
      type: s.type,
      direction: s.direction,
      windowMs: s.windowMs,
    };
  }, [sortStateIndex]);

  const sortedDscData = useMemo(() => {
    const base = Array.isArray(filteredDscData) ? filteredDscData : [];
 
    const rangeFiltered = (() => {
      if (!dateFrom && !dateTo) return base;

      const fromTsRaw =
        parseDateInputToLocalMs(dateFrom, false) ?? Number.NEGATIVE_INFINITY;
      const toTsRaw =
        parseDateInputToLocalMs(dateTo, true) ?? Number.POSITIVE_INFINITY;

      const fromTs = Math.min(fromTsRaw, toTsRaw);
      const toTs = Math.max(fromTsRaw, toTsRaw);

      return base.filter((row) => {
        const t = Date.parse(row?.termStartDate);
        return Number.isFinite(t) && t >= fromTs && t <= toTs;
      });
    })();

    if (sortConfig?.windowMs) {
      const cutoff = Date.now() - sortConfig.windowMs;
      const recentOnly = rangeFiltered.filter((row) => {
        const t = Date.parse(row?.termStartDate);
        return Number.isFinite(t) && t >= cutoff;
      });
      return sortItems(recentOnly, sortConfig);
    }

    return sortItems(rangeFiltered, sortConfig);
  }, [filteredDscData, sortConfig, dateFrom, dateTo]);
  useMountEffect(() => {
    let cancelled = false;

    const fetchDscInfo = async () => {
      setLoading(true);
      try {
        const response = await apiSession.getDscInfo();
        if (cancelled) return;

        const safeData = Array.isArray(response) ? response : [];
        const sortedByTermStartDateDesc = [...safeData].sort((a, b) => {
          const bTime = Date.parse(b?.termStartDate) || 0;
          const aTime = Date.parse(a?.termStartDate) || 0;
          return bTime - aTime;
        });

        const enriched = sortedByTermStartDateDesc.map(enrichDscRow);
        setDscData(enriched);
        setFilteredDscData(enriched);
      } catch (error) {
        // Intentionally swallow to keep UX consistent; loading is handled in finally.
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchDscInfo();

    return () => {
      cancelled = true;
    };
  }, [refresh]);
  const handlePageChange = (pageNumber)=> {
    if (expandedRow !== -1) {
      setExpandedRow(-1);
    }
    setTableRows((prev) => {
      const perPage = prev.itemsPerPage;
      const itemLast = pageNumber * perPage;
      const itemFirst = itemLast - perPage;

      return {
        ...prev,
        activePage: pageNumber,
        itemSliceFirst: itemFirst,
        itemSliceLast: itemLast,
      };
    });

}
   const handleSearchInput = (e) => {
    const { value } = e.target;
    let search = e.target.value;
    setSearchInput( value);
   
      const filteredData = fuseFilter(dscData, search, { keys: DSC_SEARCH_KEYS });
    setFilteredDscData(filteredData);
    if (value === "") {
      resetAssetList();
    }
    setTableRows((prev) => ({
      ...prev,
      activePage: 1,
      itemSliceFirst: 0,
      itemSliceLast: prev.itemsPerPage,
    }));
   
  };

  const handleSortChange = (e) => {
    const nextIndex = Number(e.target.value);

    if (expandedRow !== -1) {
      setExpandedRow(-1);
    }

    setSortStateIndex(Number.isFinite(nextIndex) ? nextIndex : 0);

    setTableRows((prev) => ({
      ...prev,
      activePage: 1,
      itemSliceFirst: 0,
      itemSliceLast: prev.itemsPerPage,
    }));
  };

  const handleDateFromChange = (e) => {
    const next = e.target.value;

    if (expandedRow !== -1) {
      setExpandedRow(-1);
    }

    setDateFrom(next);
    setTableRows((prev) => ({
      ...prev,
      activePage: 1,
      itemSliceFirst: 0,
      itemSliceLast: prev.itemsPerPage,
    }));
  };

  const handleDateToChange = (e) => {
    const next = e.target.value;

    if (expandedRow !== -1) {
      setExpandedRow(-1);
    }

    setDateTo(next);
    setTableRows((prev) => ({
      ...prev,
      activePage: 1,
      itemSliceFirst: 0,
      itemSliceLast: prev.itemsPerPage,
    }));
  };

  const handleClearFilters = () => {
    if (expandedRow !== -1) {
      setExpandedRow(-1);
      setIndexValue("");
    }

    setSearchInput("");
    setDateFrom("");
    setDateTo("");

    // Back to default sort: Term Start Date Desc
    setSortStateIndex(1);

    setFilteredDscData(dscData);
    setTableRows((prev) => ({
      ...prev,
      activePage: 1,
      itemSliceFirst: 0,
      itemSliceLast: prev.itemsPerPage,
    }));
  };

 const resetAssetList = () =>{
  setFilteredDscData(dscData);
  setTableRows((prev) => ({
    ...prev,
    activePage: 1,
    itemSliceFirst: 0,
    itemSliceLast: prev.itemsPerPage,
  }))
 }
  const handleRefresh = async () => {
    setLoading(true);

    try {
      const response = await apiSession.refreshData();
      if (response.status === 200) {
        setRefresh((prev) => !prev);
      }
    } catch (error) {
      // Intentionally ignore; loading handled below.
    } finally {
      setLoading(false);
    }
  };

  const exportColumns = useMemo(
    () => [
      { header: "Subscription No", key: "subscriptionNumber" },
      { header: "Customer Name", key: "customerName" },
      { header: "Country Po Reference", key: "customerPaymentRef" },
      { header: "Cost Type Reference", key: "costTypeReference" },
      { header: "Term Period", key: "currentTerm" },
      { header: "Activation Start Date", key: "termStartDate" },
      { header: "Subscription End Date", key: "termEndDate" },
      { header: "Status", key: "status" },
      { header: "Sold to", key: "soldTo" },
    ],
    []
  );

  const handleExportCsv = () => {
    exportToCsv({
      rows: sortedDscData,
      columns: exportColumns,
      filename: "DSC_export.csv",
    });
  };

  const handleExportPdf = async () => {
    setLoading(true);
    try {
      // await exportToPdf({
      //   title: "DSC Export",
      //   rows: sortedDscData,
      //   columns: exportColumns,
      //   filename: "DSC_export.pdf",
      //   orientation: "landscape",
      // });
    } finally {
      setLoading(false);
    }
  };

  const hasActiveFilters =
    Boolean(searchInput) ||
    Boolean(dateFrom) ||
    Boolean(dateTo) ||
    sortStateIndex !== 1;
  return (
    <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12 se-white-background">
      <se-loading loading={loading}></se-loading>
      <div className="row mt-2">
        <div className={`${!loading ? "col-sm-12 col-md-12 col-lg-12 col-xl-12 mb-1 p-0" : "col-sm-12 col-md-12 col-lg-12 col-xl-12 mb-1 p-0 se-block-disabled"}`}>
          <div className="form-row justify-content-end align-items-center">
            {/* <span
              className="pointer"
              style={searchInput ? {} : { display: "none" }}
            >
              <se-icon className="cheatSheetIcon se-icon icon_button ">
                data_refresh
              </se-icon>
            </span>
            &nbsp;&nbsp; */}
            <FuzzySearchInput
              className="form-control form-control-sm mr-sm-2 col-sm-2 inline"
              name="searchInput"
              value={searchInput}
              placeholder="Search"
              onChange={handleSearchInput}
              aria-label="Search"
              autoComplete="none"
            />
            <input
              type="text"
              className="form-control form-control-sm mr-sm-2 col-md-2"
              name="termStartFrom"
              value={dateFrom}
              onChange={handleDateFromChange}
              aria-label="From date"
              placeholder="From (YYYY-MM-DD)"
              inputMode="numeric"
            />
            <input
              type="text"
              className="form-control form-control-sm mr-sm-2 col-md-2"
              name="termStartTo"
              value={dateTo}
              onChange={handleDateToChange}
              aria-label="To date"
              placeholder="To (YYYY-MM-DD)"
              inputMode="numeric"
            />
            <select
              className="form-control form-control-sm mr-sm-2 col-md-2"
              name="sortDscOption"
              value={sortStateIndex}
              onChange={handleSortChange}
              aria-label="Sort"
            >
              <option value={0}>Term Start Date Asc</option>
              <option value={1}>Term Start Date Desc</option>
              <option value={2}>Last Week</option>
              <option value={3}>Last Month</option>
              <option value={4}>Last Year</option>
              <option value={5}>Subscription Number Asc</option>
              <option value={6}>Subscription Number Desc</option>
              <option value={7}>Customer Name Asc</option>
              <option value={8}>Customer Name Desc</option>
            </select>
            <div className = "col-sm-3 d-flex">
            <se-button
              option="raised"
              size="small"
              color="primary"
              // icon="action_close"
              onClick={handleClearFilters}
              disabled={!hasActiveFilters}
            >
              Clear
            </se-button>
           
            
            <se-dropdown alignment="right">
              <se-button
                slot="trigger"
                option="raised"
                size="small"
                color="primary"
                // icon="action_download_stroke"
                onClick={(e) => e?.preventDefault?.()}
                disabled={!sortedDscData?.length}
              >
                Export
              </se-button>
              <se-list option="dropdown">
                <se-list-item item="CSV" onClick={handleExportCsv}></se-list-item>
                {/* <se-divider></se-divider>
                <se-list-item item="PDF" onClick={handleExportPdf}></se-list-item> */}
              </se-list>
            </se-dropdown>
          
            <se-button
              option="raised"
              size="small"
              color="primary"
              icon="data_refresh"
              onClick={handleRefresh}
              disabled={!sortedDscData?.length}
            >
              Refresh
            </se-button>
            </div>
          </div>
        </div>
        <div className="col-sm-12 col-md-12 col-lg-12 col-xl-12  mb-1 px-0">
          <div className="table-responsive">
           
            <DscTable data={sortedDscData}
             itemSliceDetails={tableRows} 
             setRefresh={setRefresh} 
             expandedRow={expandedRow}
              setExpandedRow={setExpandedRow}
              setIndexValue={setIndexValue}
              setLoading = {setLoading}
              />
          </div>

          <div
            className="col-sm-12 col-md-12 col-lg-12 col-xl-12 border listStyle mb-1 pt-2"
             style={dscData?.length ? { display: "none" } : {}}
          >
            <p className="text-center se-font-16px-bold">No DSC data available</p>
          </div>

            {sortedDscData?.length > tableRows.itemsPerPage ? <Pagination
                            activePage={tableRows.activePage}
                            itemsCountPerPage={tableRows.itemsPerPage}
                    totalItemsCount={sortedDscData.length}
                            pageRangeDisplayed={5}
                            innerclassName="pagination pagination-users"
                            onChange={handlePageChange}
                        /> : ""
                        }
          <span className="float-right">
            count : {sortedDscData.length}
          </span>
        </div>
      </div>
    </div>
  );
};

export default Dsc;
